import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/apiService';

function GroupLetterAvatar({ name, size = 'size-16' }) {
  const initial = name ? name[0].toUpperCase() : '?';
  return (
    <span className={`inline-flex ${size} items-center justify-center rounded-full bg-primary-100 ring-2 ring-primary-200`}>
      <span className="text-xl font-bold text-primary-700">{initial}</span>
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-skin bg-surface-secondary p-8 shadow-soft animate-pulse">
      <div className="size-16 rounded-full bg-surface-tertiary" />
      <div className="h-4 w-28 rounded bg-surface-tertiary" />
      <div className="h-9 w-24 rounded-lg bg-surface-tertiary" />
    </div>
  );
}

export default function GroupSelectorPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  // Determine which endpoint to use based on role
  const endpoint = user?.role === 'supervisor' ? '/supervisor' : '/auth';

  useEffect(() => {
    let mounted = true;
    api.get(`${endpoint}/my-groups`)
      .then(({ data }) => { if (mounted) setGroups(data.groups ?? []); })
      .catch(() => { if (mounted) setError('Impossible de charger vos groupes. Veuillez reessayer.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [endpoint]);

  const handleSelect = async (groupId) => {
    if (selecting) return;
    setSelecting(groupId);
    try {
      const { data } = await api.post(`${endpoint}/switch-group/${groupId}`);
      const accessToken = data.accessToken ?? data.token;
      if (accessToken) localStorage.setItem('token', accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      navigate('/', { replace: true });
      window.location.reload();
    } catch {
      setError('Erreur lors de la selection du groupe. Veuillez reessayer.');
      setSelecting(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-12">
      <div className="mb-10 text-center">
        <h1 className="flex justify-center">
          <img src="/brand/m-bao-logo.png" alt="m-bao.fr" width={542} height={320} className="h-auto w-48 sm:w-64 dark:hidden" />
          <img src="/brand/m-bao-logo-on-dark.png" alt="m-bao.fr" width={542} height={320} className="hidden h-auto w-48 sm:w-64 dark:block" />
        </h1>
        <p className="mx-auto mt-2 max-w-68 text-center text-xs font-medium uppercase tracking-[0.2em] text-balance text-primary-900">
          Les bonnes relations font les bonnes affaires.
        </p>
        <p className="mt-6 text-lg font-semibold text-skin-primary">Choisir votre groupe</p>
        <p className="mt-1 text-sm text-skin-secondary">Vous appartenez a plusieurs groupes. Selectionnez celui dans lequel vous souhaitez entrer.</p>
      </div>
      {error && <div className="mb-6 w-full max-w-2xl rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      <div className="w-full max-w-2xl">
        {loading ? (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1,2,3].map(i => <li key={i}><SkeletonCard /></li>)}</ul>
        ) : groups.length === 0 ? (
          <div className="rounded-xl border border-skin bg-surface-secondary p-10 text-center shadow-soft"><p className="text-sm text-skin-tertiary">Aucun groupe disponible.</p></div>
        ) : (
          <ul role="list" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => {
              const isSelecting = selecting === group.id;
              return (
                <li key={group.id}>
                  <button onClick={() => handleSelect(group.id)} disabled={!!selecting}
                    className={`group flex w-full flex-col items-center gap-4 rounded-xl border bg-surface-secondary p-8 text-center shadow-soft transition-all duration-150 ${isSelecting ? 'border-primary-400 ring-2 ring-primary-300' : 'border-skin hover:border-primary-300 hover:shadow-dropdown hover:-translate-y-0.5'} disabled:cursor-not-allowed disabled:opacity-60`}>
                    {(group.logo_url ?? group.logo) ? <img src={group.logo_url ?? group.logo} alt={group.name} className="size-16 rounded-full object-cover ring-2 ring-skin" /> : <GroupLetterAvatar name={group.name} />}
                    <p className="text-sm font-semibold text-skin-primary">{group.name}</p>
                    <span className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${isSelecting ? 'bg-primary-600 text-white' : 'bg-primary-50 text-primary-700 group-hover:bg-primary-600 group-hover:text-white'}`}>
                      {isSelecting ? (<><svg className="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Chargement...</>) : 'Entrer'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="mt-12"><button onClick={logout} className="text-sm text-skin-tertiary hover:text-skin-secondary underline underline-offset-2">Se deconnecter</button></div>
    </div>
  );
}
